import Table from "@/components/Table/table";
import { useColumnImportandExportDefs } from "./ImportAndExportColumnDef";
import { useParams } from "react-router-dom";
import { useGetImportAndExportListDataQuery } from "./ImportandExportReducers";
import { MotifProgressLoader } from "@ey-xd/motif-react";
import { useCustomCellRenderers } from "./ImportandExportCustomCellConst";
import { useCallback, useMemo, useState } from "react";
import { useCase } from "@/features/case/use-case";
import TitleComplement from "./component/titleComplement";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import CustomPagination from "@/components/Pagination/CustomPagination";
import "./importAndExport.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";

function ImportAndExport() {
  const { caseId } = useParams<{
    caseId: string;
  }>();
  const caseData = useCase(caseId);
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  let tableLoadingData = customTableLoadingData;
  const { t } = useTranslation();
  const retainState = useSelector((e: any) => e.retainState.importListTabState);
  const dispatch = useDispatch();
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      CaseId: caseData?.caseId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [caseData?.caseId, paginationPageSize, retainState]);
  const paginationObj = getPaginationObj;

  let resetSortStatus = useCallback(
    (defaultSort: string) => {
      let json: any = { ...retainState.sortedColumnTypes };
      for (const key in json) {
        json[key] = { sort: null };
      }
      json[defaultSort] = { sort: "desc" };
      return json;
    },
    [retainState.sortedColumnTypes]
  );
  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "importListTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }

  const columnImportandExportDefs = useColumnImportandExportDefs();
  const { data, isLoading, isFetching, error,status } =
    useGetImportAndExportListDataQuery(paginationObj);

  const headerData = {
    title: "pages.importHistory.tableTitle",
    description: "pages.importHistory.tableDescription",
  };
  const caseRenderers = useCustomCellRenderers();
  const [searchQuery, setSearchQuery] = useState(retainState.SearchText || "");

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (searchValue.trim() === "") {
        dispatch(
          setComponentState1({
            entityName: "importListTabState",
            entityState: {
              PageNumber: 1,
              PageSize: paginationPageSize,
              SearchText: "",
              SortOrder: "desc",
              SortColumn: "CreatedOn",
              sortedColumnTypes: { ...resetSortStatus("createdOnType") },
            },
          })
        );
      } else {
        dispatch(
          setComponentState1({
            entityName: "importListTabState",
            entityState: {
              PageNumber: 1,
              PageSize: paginationPageSize,
              SearchText: searchValue,
              SortOrder: "desc",
              SortColumn: "CreatedOn",
              sortedColumnTypes: { ...resetSortStatus("createdOnType") },
            },
          })
        );
      }
      setSearchQuery(searchValue);
    },
    [paginationPageSize, resetSortStatus,dispatch]
  );
  const handleQuickSearchClear = useCallback(() => {
    setSearchQuery("");
    dispatch(
      setComponentState1({
        entityName: "importListTabState",
        entityState: {
          PageNumber: 1,
          PageSize: paginationPageSize,
          SearchText: "",
          SortOrder: "desc",
          SortColumn: "CreatedOn",
          sortedColumnTypes: { ...resetSortStatus("createdOnType") },
        },
      })
    );
  }, [paginationPageSize, resetSortStatus,dispatch]);

  if (isLoading) {
    return <div> {t("global.globalNotifications.loadingMessage")} </div>;
  }
  const tableData = data?.results ? data.results : [];
  return (
    <>
      {isLoading ? (
        <MotifProgressLoader show variant="default" className="mar-b-20" />
      ) : (
        <div className="mar-b-20 import-export-table">
          <Table
            columnDefs={columnImportandExportDefs}
            columnTypes={{ ...retainState.sortedColumnTypes }}
            handlePaginationData={handlePaginationData}
            paginationObj={paginationObj}
            cellRenderers={isFetching ? [] : tableData}
            TitleComplement={() => (
              <TitleComplement
                onSearch={handleSearch}
                handleQuickSearchClear={handleQuickSearchClear}
                searchQuery={searchQuery}
              />
            )}
            title={headerData.title}
            description={headerData.description}
            caseRenderers={caseRenderers}
            pagination={false}
            overlayNoRowsTemplate={
              isFetching
                ? t(tableLoadingData)
                : error
                ? t("global.globalNotifications.wentWrong")
                : t("global.globalNotifications.noRecords")
            }
          />
          <CustomPagination
             apiData={data}
             apiStatus={status}
            currentpage={paginationObj.PageNumber}
            pageSize={paginationObj.PageSize}
            disableNext={!data?.hasMoreRecords}
            onChangePageSize={(page: number) => {
              dispatch(
                setComponentState1({
                  entityName: "importListTabState",
                  entityState: {
                    PageSize: page,
                    PageNumber: 1,
                  },
                })
              );
            }}
            onChangeNextPage={() => {
              dispatch(
                setComponentState1({
                  entityName: "importListTabState",
                  entityState: {
                    PageNumber: paginationObj.PageNumber + 1,
                  },
                })
              );
            }}
            onChangePreviousPage={() => {
              dispatch(
                setComponentState1({
                  entityName: "importListTabState",
                  entityState: {
                    PageNumber: paginationObj.PageNumber - 1,
                  },
                })
              );
            }}
            pageSizeSelector={paginationPageSizeSelector}
          />
        </div>
      )}
    </>
  );
}
export default ImportAndExport;
